import type { PresetV2 } from "./mockData";

// Feature flag del modo "Video con influencer". Mientras esté en `false` el
// modo queda OCULTO para TODOS — tanto en el design-row de la home V2 como
// en la pantalla de Modos. Con `true` es visible para todos los planes
// (ADR-561): la restricción Pro/Ultra aplica al GENERAR
// (VideoPlanRestrictionModal vía checkGenerationGuards).
export const VIDEO_INFLUENCER_ENABLED = true;

// Los modos "Catálogo de muebles" (ADR-582) e "Infografías" (ADR-814) YA NO
// tienen feature flag: se habilitaron y ahora se gatean por el rubro de la
// MARCA activa — ver `soloHogarDeco` acá abajo y
// ADR-2026-07-31-modos-muebles-por-rubro-de-marca.

// Catálogo de "modos" de generación de Interfaz V2 (los mismos que aparecen en
// la home V2 / el design-row). Centralizado acá para reusarlo en la pantalla
// de Modos.
//
// Cada modo incluye el objeto `preset` EXACTO que usa InterfazV2 para abrir el
// sheet del modo (mismo id/kind/imágenes que las cards sintéticas de la home).
// Se pasa como `state.presetData` al navegar para que InterfazV2 monte el sheet
// en el primer paint (mecanismo `initialNavPreset`) y no se vea un flash de la
// home V2.

export interface ModeCard {
  /** Segmento de ruta del modo en V2: `/interfaz-v2/modo/<path>`. */
  path: string;
  /** Carátula en video (mp4 corto, autoplay loop muteado). Opcional: los modos
   *  sin video propio (catálogo-muebles, infografía) muestran `preset.imageUrl`
   *  estático en su lugar (los consumidores caen a `<img>` si no hay video). */
  video?: string;
  /** Imagen de fallback mientras carga el video. */
  poster?: string;
  /** Preset que abre el modo en V2 (espeja las cards del design-row). */
  preset: PresetV2;
  /** Si true, el modo solo se muestra a dev testers (los consumidores de
   *  `MODES` deben filtrar con `isDevTester`). */
  devOnly?: boolean;
  /** Si true, el modo solo se muestra cuando la MARCA activa es del rubro
   *  "Hogar y Decoración" (los consumidores de `MODES` deben filtrar con
   *  `esRubroHogarDeco(rubro de useRubroMarcaActiva)`). Reemplaza a los viejos
   *  flags CATALOGO_MUEBLES_ENABLED / INFOGRAFIA_ENABLED —
   *  ADR-2026-07-31-modos-muebles-por-rubro-de-marca. */
  soloHogarDeco?: boolean;
  /** Inverso de `soloHogarDeco`: el modo se OCULTA cuando la marca activa es de
   *  "Hogar y Decoración". Para los modos que ponen el producto sobre un modelo
   *  IA, que no aplican a un mueble —
   *  ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo. */
  ocultoEnHogarDeco?: boolean;
}

// Orden igual al design-row de la home V2: [auto, modelo, catálogo,
// video-influencer, asistente, anuncio].
export const MODES: ModeCard[] = [
  {
    path: "auto",
    video: "/images/modo_automatico/modo_auto_caratula_desktop.mp4",
    poster: "/images/modo_automatico/modo_auto_caratula.png",
    preset: {
      id: "imagen-auto-card",
      title: "Generar imagen automáticamente",
      description:
        "Sumá tu producto y la IA arma todo: fondo, escena y luz por vos.",
      imageUrl: "/images/modo_automatico/modo_auto_caratula.png",
      imageUrlFull: "/images/modo_automatico/modo_auto.png",
      kind: "design-auto",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    path: "modelo",
    video: "/images/modo_con_modelo/modo_modelo_caratula_desktop.mp4",
    poster: "/images/modo_con_modelo/fot_modelo.png",
    preset: {
      id: "imagen-con-modelo",
      title: "Generar imagen personalizada",
      description:
        "Tu producto sobre un modelo IA en la pose y el fondo que vos elijas.",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_con_modelo/modelo_gia.png",
      kind: "design-modelo",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // El producto va sobre un modelo IA: no aplica a muebles.
    ocultoEnHogarDeco: true,
  },
  {
    path: "catalogo",
    video: "/images/modo_catalogo_ia/modo_catalogo_caratula_desktop.mp4",
    poster: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
    preset: {
      id: "catalogo-ia-card",
      title: "Generar catálogo con IA",
      description:
        "Photoshoots por producto con varias vistas y poses en un solo proceso.",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "catalogo-ia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // Su reemplazo para hogar/deco es el catálogo de muebles, que se muestra con
    // este mismo título — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
    ocultoEnHogarDeco: true,
  },
  {
    // Modo "Catálogo de muebles" (ADR-582). Visible solo para marcas del rubro
    // "Hogar y Decoración" (`soloHogarDeco`) — los consumidores de MODES tienen
    // que filtrarlo, igual que con `devOnly`.
    //
    // Card SIN video propio: muestra `imageUrl` estático (el ropero en contexto,
    // ADR-817). Sin `video`/`poster` → Modos.tsx y HomeCards caen a `<img>`. El
    // `imageUrlFull` (hero de "Elegí el producto") lo reemplaza el collage
    // `ProductHeroCollage`, así que el que quede acá no se ve.
    path: "catalogo-muebles",
    preset: {
      id: "catalogo-muebles-card",
      // Se llama igual que el Catálogo IA a propósito: para una marca de Hogar y
      // Decoración ESTE es su catálogo con IA (el otro modo no se le muestra), así
      // que el nombre "de muebles" era una distinción interna que no le decía nada
      // — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
      title: "Generar catálogo con IA",
      description:
        "Fondo blanco, infografía con medidas y foto en contexto, para cada uno de tus productos.",
      imageUrl: "/images/modo_catalogo_muebles/hero_contexto.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "catalogo-muebles",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Infografía" (ADR-813): fichas técnicas dimensionales de los productos.
    // Reusa el flujo y el motor del catálogo de muebles pero salteando el picker de
    // vistas (su única vista es la infografía). Mismo gate de rubro de marca
    // que el catálogo de muebles (`soloHogarDeco`).
    // Card SIN video propio: muestra `imageUrl` estático (una infografía con cotas,
    // ADR-817); el hero de "Elegí el producto" lo pone ProductHeroCollage.
    path: "infografia",
    preset: {
      id: "infografia-card",
      title: "Infografías de producto con IA",
      description:
        "Fichas técnicas con las medidas y características de cada uno de tus productos.",
      imageUrl: "/images/modo_infografia/hero_foto.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "infografia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Video con influencer" — visible para TODOS los planes desde
    // ADR-561 (con candado + bloqueo de entrada para gratis/Básico vía
    // getModePlanLock, ADR-564). Carátula en video LANDSCAPE propia (16:9,
    // para el slot 3:2 de la página Modos) + su poster. El hero MOBILE usa
    // otro asset vertical (720x960). La miniatura (portrait) es la imagen
    // "al lado de la explicación". Ver ADR-506 / ADR-509.
    path: "video-influencer",
    video: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.mp4",
    poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.png",
    preset: {
      id: "video-influencer-card",
      title: "Video con influencer",
      description:
        "Generá un video de tu producto presentado por un influencer IA, con un gancho que enganche en los primeros segundos.",
      imageUrl: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      imageUrlFull: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.png",
      kind: "video-influencer",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    path: "asistente",
    video: "/images/modo_asistente/modo_asistente_caratula_desktop.mp4",
    poster: "/images/modo_asistente/modo_asistente_caratula_mobile.png",
    preset: {
      id: "asistente-marketing-card",
      title: "Crear desde cero",
      description: "Generá, analizá y planificá tu contenido",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "asistente",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    path: "anuncio",
    video: "/images/modo_anuncio/modo_anuncio_caratula_desktop.mp4",
    poster: "/images/modo_anuncio/modo_anuncio_hero.png",
    preset: {
      id: "anuncio-card",
      title: "Anuncio",
      description:
        "Texto, colores y formato — armá un anuncio listo para publicar.",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_anuncio/modo_anuncio_hero.png",
      kind: "anuncio",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
];
